import {Dropdown, Text, TextField} from '@/components/atoms'
import {usePromotion} from '@/context/Promotion/PromotionContext'
import {selectCountryCode} from '@/store/slices/countryCodeSlice'
import {CurrencyType} from '@/types/promotion'
import {Constants} from '@/utils/constants/commonConstants'
import {ANY_BUY_OPTION, ANY_ID} from '@/utils/constants/promotionConstants'
import {useMemo, useState} from 'react'
import {useSelector} from 'react-redux'
import PromotionSelectionModal from '../PromotionSelectionModal'
import {RewardItem} from './Rewards/RewardItem'
import {useRewardValidations} from './Rewards/RewardValidations'

const defaultCondition = {
  type: 'BUY',
  items: [],
}

export const BuyMultipleProductsCondition = ({promotionCondition, index, selectedPromotionCondition = 'UN'}) => {
  const {updatePromotion, promotionConfig, isLoadingConfig} = usePromotion()
  const {hasBuyQuantityLayerError} = useRewardValidations(promotionCondition)
  const isAmountTab = selectedPromotionCondition === 'EA'
  const countryCode = useSelector(selectCountryCode)
  const currencyCode =  CurrencyType[countryCode ?? 'MX'] ?? '$'
  const [modalProps, setModalProps] = useState({
    show: false,
    type: '',
    index: 0,
  })

  const buyQtyOptions = useMemo(
    () => promotionConfig?.buyQty?.map(value => ({displayText: value, id: Number(value)})),
    [promotionConfig?.buyQty]
  )

  const buyTypeOptions = promotionConfig?.conditions?.itemTypeOptions ?? []

  if (!promotionCondition) {
    return null
  }

  const [rawCond1, rawCond2] = promotionCondition.conditionGroup?.conditions || []
  const cond1 = rawCond1 ?? defaultCondition
  const cond2 = rawCond2 ?? defaultCondition
  const handleBuyCount = (values, index = 0) => {
    const value = values?.[0]?.id
    if (value) {
      // If ANY is selected in the quantity dropdown, set quantity to 1
      if (isNaN(value) && value === ANY_ID) {
        updateConditionByIndex(isAmountTab ? {amount: 1} : {quantity: 1}, index)
      } else {
        const nextValue = Number(value)
        updateConditionByIndex(isAmountTab ? {amount: nextValue} : {quantity: nextValue}, index)
      }
    }
  }

  const handleBuyAmount = (value, condIndex) => {
    updateConditionByIndex({amount: value ? Number(value) : null}, condIndex)
  }

  const updateConditionByIndex = (update, conditionIndex = 0) => {
    updatePromotion(prev => {
      const updatedConditions = prev.promotionConditions.map(condition => {
        if (condition.sequence !== promotionCondition.sequence) return condition

        const clonedCondition = JSON.parse(JSON.stringify(condition))

        if (clonedCondition.conditionGroup?.conditions?.[conditionIndex]) {
          clonedCondition.conditionGroup.conditions[conditionIndex] = {
            ...clonedCondition.conditionGroup.conditions[conditionIndex],
            ...update,
          }
        }

        return clonedCondition
      })

      return {
        ...prev,
        promotionConditions: updatedConditions,
      }
    })
  }

  const clearValues = index => {
    updateConditionByIndex({items: [], itemType: null}, index)
  }

  const handleSelectedBuyItem = (values, index) => {
    if (!values || !values.length) {
      clearValues(index)
      return
    }

    const isModalValue = values.find(item => buyTypeOptions.find(({id}) => id === item.id))

    if (isModalValue) {
      if (isModalValue.id === ANY_ID) {
        handleValues({values: [ANY_BUY_OPTION], itemType: ANY_ID}, null, index)
      } else {
        setModalProps({show: true, type: isModalValue.id, index})
      }
    } else {
      // Directly update when user adds/removes regular items
      const currentItemType = index === 0 ? cond1.itemType : cond2.itemType
      handleValues({values, itemType: currentItemType}, null, index)
    }
  }

  const handleCloseModal = () => {
    setModalProps(prev => ({...prev, show: false}))
  }

  const handleValues = (selectedItemsAndType, productCombination, index = modalProps.index) => {
    const {values, itemType} = selectedItemsAndType || {}
    if (!values || !Array.isArray(values)) {
      clearValues(index)
      return
    }
    const items = values.map(item => ({id: item?.id, name: item?.displayText, uom: selectedPromotionCondition}))

    updateConditionByIndex(
      {
        items,
        productCombination,
        itemType,
      },
      index
    )
  }

  const handleSubmitModal = (selectedItems, combination) => {
    handleValues(selectedItems, combination)
    handleCloseModal()
  }

  const buyCount1 = isAmountTab ? cond1.amount : cond1.quantity
  const items1 = cond1?.items ?? []

  const selectedValuesText1 = items1.map(item => item.name).join(', ')
  const quantityValue1 = buyQtyOptions?.filter(item => item.id === buyCount1) ?? []
  const productsValue1 = items1.map(item => ({id: item.id, displayText: item.name})) ?? []

  const buyCount2 = isAmountTab ? cond2.amount : cond2.quantity
  const items2 = cond2?.items ?? []

  const selectedValuesText2 = items2.map(item => item.name).join(', ')
  const quantityValue2 = buyQtyOptions?.filter(item => item.id === buyCount2) ?? []
  const productsValue2 = items2.map(item => ({id: item.id, displayText: item.name})) ?? []

  const productsValue1HasAny = productsValue1?.find(value => value.id === ANY_ID)
  const butyQtyOptions1 = productsValue1HasAny ? buyQtyOptions?.filter(item => item.id !== ANY_ID) : buyQtyOptions

  const quantityValue1HasAny = quantityValue1?.find(value => value.id === ANY_ID)
  const buyTypeOptions1 = quantityValue1HasAny ? buyTypeOptions?.filter(item => item.id !== ANY_ID) : buyTypeOptions

  const productsValue2HasAny = productsValue2?.find(value => value.id === ANY_ID)
  const butyQtyOptions2 = productsValue2HasAny ? buyQtyOptions?.filter(item => item.id !== ANY_ID) : buyQtyOptions

  const quantityValue2HasAny = quantityValue2?.find(value => value.id === ANY_ID)
  const buyTypeOptions2 = quantityValue2HasAny ? buyTypeOptions?.filter(item => item.id !== ANY_ID) : buyTypeOptions

  const isError1 = hasBuyQuantityLayerError(0)
  const isError2 = hasBuyQuantityLayerError(1)
  return (
    <>
      <Text>Buy</Text>
      {isAmountTab ? (
        <>
          <Text>{currencyCode}</Text>
          <TextField
            updateValue={value => handleBuyAmount(value, 0)}
            value={buyCount1?.toString() || ''}
            error={isError1 ? 'Value Invalid' : ''}
            uiSchema={{helperText: '', placeholder: '0.0'}}
            style={{maxWidth: '85px'}}
            type="number"
          />
        </>
      ) : (
        <Dropdown
          isJsonWrap={false}
          childList={butyQtyOptions1}
          placeholder={ANY_ID}
          updateValue={values => handleBuyCount(values, 0)}
          isClearable={false}
          value={quantityValue1}
          autocomplete="off"
          style={{width: 84}}
          loading={isLoadingConfig}
          isError={isError1}
          errorMessage="Value Invalid"
        />
      )}
      <Dropdown
        isJsonWrap={false}
        childList={buyTypeOptions1}
        placeholder={selectedValuesText1 || Constants.AddProducts}
        value={[...productsValue1]}
        updateValue={values => handleSelectedBuyItem(values, 0)}
        selection="multiple"
        isClearable={true}
        autocomplete="off"
        loading={isLoadingConfig}
        disabled={index > 0}
        className={index > 0 ? 'disable-dropdown-idx' : ''}
      />
      <Text>{'and buy'}</Text>
      {isAmountTab ? (
        <>
          <Text>{currencyCode}</Text>
          <TextField
            updateValue={value => handleBuyAmount(value, 1)}
            value={buyCount2?.toString() || ''}
            error={isError2 ? 'Value Invalid' : ''}
            uiSchema={{helperText: '', placeholder: '0.0'}}
            style={{maxWidth: '85px'}}
            type="number"
          />
        </>
      ) : (
        <Dropdown
          isJsonWrap={false}
          childList={butyQtyOptions2}
          placeholder={ANY_ID}
          updateValue={values => handleBuyCount(values, 1)}
          isClearable={false}
          value={quantityValue2}
          autocomplete="off"
          style={{width: 84}}
          loading={isLoadingConfig}
          isError={isError2}
          errorMessage="Value Invalid"
        />
      )}
      <Dropdown
        isJsonWrap={false}
        childList={buyTypeOptions2}
        placeholder={selectedValuesText2 || Constants.AddProducts}
        value={[...productsValue2]}
        updateValue={values => handleSelectedBuyItem(values, 1)}
        selection="multiple"
        isClearable={true}
        autocomplete="off"
        loading={isLoadingConfig}
        disabled={index > 0}
        className={index > 0 ? 'disable-dropdown-idx' : ''}
      />
      <Text>{'and get'}</Text>
      <RewardItem promotionCondition={promotionCondition} index={index} />
      <PromotionSelectionModal
        key={JSON.stringify(modalProps)}
        show={modalProps.show}
        type={modalProps.type}
        onClose={handleCloseModal}
        onSubmit={handleSubmitModal}
        conditionValues={{
          values: modalProps.index === 0 ? productsValue1 : productsValue2,
          itemType: modalProps.index === 0 ? cond1.itemType : cond2.itemType,
          productCombination: modalProps.index === 0 ? cond1.productCombination : cond2.productCombination,
        }}
      />
    </>
  )
}
